import React, { useMemo } from 'react';
import styled from 'styled-components';
import useWindowWidth from '@hooks/useWindowWidth';
import { IMainGrid } from '@typings/setting/MainHomeSecType';
import {
  generateGridTemplateAreas,
  groupAndSortByStatus,
  generateMobileGridTemplateAreas,
  generateMobileGridTemplateRows,
  generateGridTemplateRows,
} from './customGridUtils';
import { sanitizeGridAreaName } from '@utils/sanitizeGridAreaName';

const GameUIContainer = styled.div`
  width: 100%;
  height: auto;

  max-width: 100%;
  box-sizing: border-box;

  @media (max-width: 480px) {
    padding: 8px;
  }
`;

interface GameUiGridSystemProps {
  // 그리드 데이터
  customGrid?: IMainGrid;
  customGridSize?: { rows: number; cols: number };
  defaultGrid?: IMainGrid;
  defaultGridSize?: { rows: number; cols: number };

  // 렌더링 컴포넌트 맵
  renderComponentMap: Record<
    string,
    (props: { key: React.Key }) => JSX.Element
  >;

  // 추가 컨테이너 스타일
  containerStyle?: React.CSSProperties;
  containerClassName?: string;

  // 모바일 브레이크포인트 (기본값: 480px)
  mobileBreakpoint?: number;
}

function GameUiGridSystem({
  customGrid,
  customGridSize,
  defaultGrid,
  defaultGridSize = { rows: 2, cols: 5 },
  renderComponentMap,
  containerStyle = {},
  containerClassName = '',
  mobileBreakpoint = 480,
}: GameUiGridSystemProps) {
  const windowWidth = useWindowWidth();

  // 그리드 데이터 결정
  const gridData = customGrid || defaultGrid;
  const gridSize = customGridSize || defaultGridSize;

  // 📍 컨테이너 크기에 따른 동적 그리드 조정
  const containerMaxWidth = containerStyle.maxWidth || '100%';
  const isSmallContainer =
    (typeof containerMaxWidth === 'string' &&
      containerMaxWidth.includes('px') &&
      parseInt(containerMaxWidth) < 300) ||
    windowWidth < mobileBreakpoint;

  // 그리드 데이터가 없으면 빈 컨테이너 반환
  if (!gridData) {
    return (
      <GameUIContainer className={containerClassName} style={containerStyle}>
        <div>설정된 메뉴가 없습니다.</div>
      </GameUIContainer>
    );
  }

  // 상태별 그룹화 및 정렬
  const groupedByStatusData = useMemo(() => {
    return groupAndSortByStatus(gridData);
  }, [gridData]);

  // GameUI용 그리드 템플릿 영역 생성 (footer 제외)
  const generateGameUIGridTemplateAreas = useMemo(() => {
    const grid: string[][] = Array(gridSize.rows)
      .fill(null)
      .map(() => Array(gridSize.cols).fill('.')); // 빈 그리드 초기화
    const dataArray = Object.values(gridData);

    // 데이터를 grid에 매핑
    dataArray.forEach((group) => {
      group.cells.forEach(({ row, col }) => {
        // row, col 값이 grid의 범위 내에 있는지 확인
        if (
          row >= 0 &&
          row < gridSize.rows &&
          col >= 0 &&
          col < gridSize.cols
        ) {
          grid[row][col] = sanitizeGridAreaName(group.status);
        }
      });
    });

    // grid-template-areas 형식으로 변환 (footer 제외)
    const templateAreas = grid
      .map((row) => `'${row.join(' ').trim()}'`)
      .join('\n');

    return templateAreas;
  }, [gridData, gridSize]);

  // 모바일 그리드 템플릿 영역
  const gridMobileTemplateAreas = useMemo(() => {
    return generateMobileGridTemplateAreas({
      groupedByStatus: groupedByStatusData,
      gridSize,
      data: gridData,
    });
  }, [groupedByStatusData, gridSize, gridData]);

  // GameUI용 그리드 행 설정 (footer 제외)
  const generateGameUIGridTemplateRows = useMemo(() => {
    const rows = generateGameUIGridTemplateAreas.trim().split('\n');
    const rowValues = rows.map(() => 'auto'); // GameUI에서는 모든 행을 auto로 설정
    return rowValues.join(' ');
  }, [generateGameUIGridTemplateAreas]);

  // 모바일 그리드 행 설정
  const gridMobileTemplateRows = useMemo(() => {
    return generateMobileGridTemplateRows(
      groupedByStatusData,
      generateGameUIGridTemplateAreas,
    );
  }, [groupedByStatusData, generateGameUIGridTemplateAreas]);

  // 반응형 여부 결정
  const isDesktop = windowWidth > mobileBreakpoint;

  return (
    <GameUIContainer
      className={containerClassName}
      data-small={isSmallContainer}
      style={{
        display: 'grid',
        gridTemplateColumns: isDesktop
          ? `repeat(${gridSize.cols}, 1fr)`
          : '1fr',
        gridTemplateAreas: isDesktop
          ? generateGameUIGridTemplateAreas
          : gridMobileTemplateAreas,
        gridTemplateRows: isDesktop
          ? generateGameUIGridTemplateRows
          : gridMobileTemplateRows,
        // 📍 max-width 제약 조건 적용
        maxWidth: containerStyle.maxWidth || '100%',
        ...containerStyle,
      }}
    >
      {/* 그리드 아이템 렌더링 */}
      {Object.keys(renderComponentMap).map((status, i) => {
        const renderComponent = renderComponentMap[status];

        return renderComponent
          ? renderComponent({ key: `${status}-${i}` })
          : null;
      })}
    </GameUIContainer>
  );
}

export default GameUiGridSystem;
